import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InquiryErrors, InquiryField } from '@/lib/inquiry'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { BUDGET_OPTIONS, SERVICE_OPTIONS } from '@/constants/landing'

import {
    ArrowUpRight,
    Check,
    CircleAlert,
    CircleCheck,
    LoaderCircle
} from 'lucide-react'

import {
    INQUIRY_FIELDS,
    INQUIRY_LIMITS,
    hasErrors,
    validateInquiry
} from '@/lib/inquiry'

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

type TextFieldName = 'name' | 'email' | 'company' | 'message'

interface FormValues {
    name: string
    email: string
    company: string
    services: Array<string>
    budget: string
    message: string
}

interface ContactResponse {
    ok?: boolean
    message?: string
    errors?: InquiryErrors
}

interface FieldErrorProps {
    field: InquiryField
    message?: string
}

interface TextFieldProps {
    field: 'name' | 'email' | 'company'
    label: string
    type?: string
    inputMode?: 'text' | 'email'
    autoComplete: string
    placeholder: string
    required?: boolean
    maxLength: number
    value: string
    error?: string
    onChange: (event: ChangeEvent<HTMLInputElement>) => void
}

const INITIAL_VALUES: FormValues = {
    name: '',
    email: '',
    company: '',
    services: [],
    budget: '',
    message: ''
}

const LABEL = 'font-mono text-xs font-medium tracking-[0.16em] uppercase'

const FIELD = 'w-full rounded-2xl border-2 border-foreground/15 bg-card px-4 text-base text-foreground transition-[border-color,box-shadow] placeholder:text-muted-foreground hover:border-foreground/40 focus:border-foreground focus:ring-4 focus:ring-primary/30 focus:outline-none aria-[invalid=true]:border-destructive'

const CHIP = 'inline-flex cursor-pointer items-center gap-1.5 rounded-full border-2 px-4 py-2.5 text-sm font-semibold transition-colors select-none has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-foreground'

const fieldId = (field: InquiryField): string => `inquiry-${field}`

const errorId = (field: InquiryField): string => `inquiry-${field}-error`

const FieldError: FC<FieldErrorProps> = ({ field, message }): ReactNode => {
    if (!message) return null

    return (
        <p id={errorId(field)} className='mt-2 flex items-center gap-1.5 text-sm font-medium text-destructive'>
            <CircleAlert aria-hidden='true' className='size-4 shrink-0' />
            {message}
        </p>
    )
}

const TextField: FC<TextFieldProps> = ({
    field,
    label,
    type = 'text',
    inputMode = 'text',
    autoComplete,
    placeholder,
    required = false,
    maxLength,
    value,
    error,
    onChange
}): ReactNode => {
    return (
        <div>
            <label htmlFor={fieldId(field)} className={LABEL}>
                {label}
                {required && <span aria-hidden='true' className='text-destructive'> *</span>}
            </label>

            <input
                id={fieldId(field)}
                name={field}
                type={type}
                inputMode={inputMode}
                autoComplete={autoComplete}
                placeholder={placeholder}
                maxLength={maxLength}
                value={value}
                onChange={onChange}
                aria-required={required || undefined}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId(field) : undefined}
                className={cn(FIELD, 'mt-2 h-14')}
            />

            <FieldError field={field} message={error} />
        </div>
    )
}

const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<InquiryErrors>({})
    const [status, setStatus] = useState<FormStatus>('idle')
    const [notice, setNotice] = useState('')
    const startedAtRef = useRef(0)
    const honeypotRef = useRef<HTMLInputElement>(null)
    const successRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        startedAtRef.current = Date.now()
    }, [])

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    const clearError = (field: InquiryField) => {
        setErrors(current => {
            if (!current[field]) return current

            const next = { ...current }
            delete next[field]

            return next
        })
    }

    const handleTextChange = (field: TextFieldName) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))
        clearError(field)
    }

    const toggleService = (option: string) => {
        setValues(current => ({
            ...current,
            services: current.services.includes(option)
                ? current.services.filter(service => service !== option)
                : [...current.services, option]
        }))
        clearError('services')
    }

    const selectBudget = (option: string) => {
        setValues(current => ({ ...current, budget: option }))
        clearError('budget')
    }

    const focusFirstError = (found: InquiryErrors) => {
        const first = INQUIRY_FIELDS.find(field => found[field])

        if (first) document.getElementById(fieldId(first))?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'submitting') return

        const { errors: found } = validateInquiry(values)

        if (hasErrors(found)) {
            setErrors(found)
            setStatus('idle')
            setNotice('')
            focusFirstError(found)
            return
        }

        setStatus('submitting')
        setNotice('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...values,
                    website: honeypotRef.current?.value ?? '',
                    elapsed: startedAtRef.current ? Date.now() - startedAtRef.current : null
                })
            })

            const payload = await response.json().catch(() => null) as ContactResponse | null

            if (response.ok && payload?.ok) {
                setValues(INITIAL_VALUES)
                setErrors({})
                setStatus('success')
                return
            }

            if (payload?.errors && hasErrors(payload.errors)) {
                setErrors(payload.errors)
                focusFirstError(payload.errors)
            }

            setNotice(payload?.message || 'Something went wrong on our side. Please try again in a minute.')
            setStatus('error')
        }

        catch {
            setNotice('We could not reach the studio. Check your connection and try again.')
            setStatus('error')
        }
    }

    const handleReset = () => {
        setStatus('idle')
        setNotice('')
        startedAtRef.current = Date.now()
        window.requestAnimationFrame(() => document.getElementById(fieldId('name'))?.focus())
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='flex min-h-[34rem] flex-col items-start justify-center rounded-[2rem] bg-pop-yellow p-8 text-foreground focus:outline-none md:p-12'
            >
                <CircleCheck aria-hidden='true' strokeWidth={1.75} className='size-12' />

                <p className='mt-6 font-heading text-[clamp(2.5rem,5vw,4.5rem)] leading-[0.88] font-black uppercase wdth-75'>
                    Got it. Your brief is in.
                </p>

                <p className='mt-5 max-w-md text-lg leading-relaxed'>
                    Thanks for reaching out. A partner from the studio will reply within two working days, usually sooner.
                </p>

                <button
                    type='button'
                    onClick={handleReset}
                    className={buttonStyles({ variant: 'ink', className: 'mt-8' })}
                >
                    Send another brief
                </button>
            </div>
        )
    }

    const isSubmitting = status === 'submitting'

    return (
        <form
            noValidate
            onSubmit={handleSubmit}
            aria-labelledby='inquiry-title'
            className='rounded-[2rem] bg-background p-6 text-foreground md:p-10'
        >
            <h3 id='inquiry-title' className='font-heading text-2xl leading-tight font-black uppercase wdth-90 md:text-3xl'>
                Tell us about the brand
            </h3>

            <p className='mt-2 text-sm text-muted-foreground'>
                Fields marked <span aria-hidden='true' className='font-bold text-destructive'>*</span><span className='sr-only'>with an asterisk</span> are required.
            </p>

            <div className='mt-8 grid gap-6 sm:grid-cols-2'>
                <TextField
                    field='name'
                    label='Your name'
                    autoComplete='name'
                    placeholder='Ada Lovelace'
                    required
                    maxLength={INQUIRY_LIMITS.name}
                    value={values.name}
                    error={errors.name}
                    onChange={handleTextChange('name')}
                />

                <TextField
                    field='email'
                    label='Email'
                    type='email'
                    inputMode='email'
                    autoComplete='email'
                    placeholder='ada@yourbrand.com'
                    required
                    maxLength={INQUIRY_LIMITS.email}
                    value={values.email}
                    error={errors.email}
                    onChange={handleTextChange('email')}
                />

                <div className='sm:col-span-2'>
                    <TextField
                        field='company'
                        label='Brand or company'
                        autoComplete='organization'
                        placeholder='What should we call it?'
                        maxLength={INQUIRY_LIMITS.company}
                        value={values.company}
                        error={errors.company}
                        onChange={handleTextChange('company')}
                    />
                </div>

                <fieldset className='sm:col-span-2'>
                    <legend className={LABEL}>
                        What do you need? <span className='tracking-normal text-muted-foreground normal-case'>(pick any)</span>
                    </legend>

                    <div className='mt-3 flex flex-wrap gap-2'>
                        {SERVICE_OPTIONS.map((option, index) => {
                            const isChecked = values.services.includes(option)

                            return (
                                <label
                                    key={option}
                                    className={cn(
                                        CHIP,
                                        isChecked
                                            ? 'border-foreground bg-foreground text-background'
                                            : 'border-foreground/15 bg-card hover:border-foreground/50'
                                    )}
                                >
                                    <input
                                        id={index === 0 ? fieldId('services') : undefined}
                                        type='checkbox'
                                        name='services'
                                        value={option}
                                        checked={isChecked}
                                        onChange={() => toggleService(option)}
                                        className='sr-only'
                                    />
                                    {isChecked && <Check aria-hidden='true' className='size-4' />}
                                    {option}
                                </label>
                            )
                        })}
                    </div>

                    <FieldError field='services' message={errors.services} />
                </fieldset>

                <fieldset
                    className='sm:col-span-2'
                    aria-describedby={errors.budget ? errorId('budget') : undefined}
                >
                    <legend className={LABEL}>
                        Budget <span className='tracking-normal text-muted-foreground normal-case'>(a rough range is fine)</span>
                    </legend>

                    <div className='mt-3 flex flex-wrap gap-2'>
                        {BUDGET_OPTIONS.map((option, index) => {
                            const isChecked = values.budget === option

                            return (
                                <label
                                    key={option}
                                    className={cn(
                                        CHIP,
                                        isChecked
                                            ? 'border-foreground bg-foreground text-background'
                                            : 'border-foreground/15 bg-card hover:border-foreground/50'
                                    )}
                                >
                                    <input
                                        id={index === 0 ? fieldId('budget') : undefined}
                                        type='radio'
                                        name='budget'
                                        value={option}
                                        checked={isChecked}
                                        onChange={() => selectBudget(option)}
                                        className='sr-only'
                                    />
                                    {option}
                                </label>
                            )
                        })}
                    </div>

                    <FieldError field='budget' message={errors.budget} />
                </fieldset>

                <div className='sm:col-span-2'>
                    <label htmlFor={fieldId('message')} className={LABEL}>
                        The brief
                        <span aria-hidden='true' className='text-destructive'> *</span>
                    </label>

                    <textarea
                        id={fieldId('message')}
                        name='message'
                        rows={6}
                        placeholder='What are you launching, where will it sell, and when does it need to hit the shelf?'
                        maxLength={INQUIRY_LIMITS.messageMax}
                        value={values.message}
                        onChange={handleTextChange('message')}
                        aria-required='true'
                        aria-invalid={errors.message ? true : undefined}
                        aria-describedby={errors.message ? `${errorId('message')} inquiry-message-count` : 'inquiry-message-count'}
                        className={cn(FIELD, 'mt-2 min-h-40 resize-y py-4 leading-relaxed')}
                    />

                    <div className='flex items-start justify-between gap-4'>
                        <FieldError field='message' message={errors.message} />

                        <p id='inquiry-message-count' className='mt-2 ml-auto font-mono text-xs text-muted-foreground tabular-nums'>
                            {values.message.length} / 4,000
                        </p>
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='sr-only'>
                <label>
                    Leave this field empty
                    <input ref={honeypotRef} type='text' name='website' tabIndex={-1} autoComplete='off' defaultValue='' />
                </label>
            </div>

            {status === 'error' && notice && (
                <div role='alert' className='mt-6 flex items-start gap-3 rounded-2xl border-2 border-destructive bg-destructive/10 p-4 text-sm font-medium text-destructive'>
                    <CircleAlert aria-hidden='true' className='mt-0.5 size-5 shrink-0' />
                    <p>
                        {notice}
                    </p>
                </div>
            )}

            <div className='mt-8 flex flex-col-reverse items-start gap-5 sm:flex-row sm:items-center sm:justify-between'>
                <p className='max-w-xs text-sm leading-relaxed text-muted-foreground'>
                    We only use your details to reply to you. No newsletters, ever.
                </p>

                <button
                    type='submit'
                    disabled={isSubmitting}
                    className={buttonStyles({ size: 'lg' })}
                >
                    {isSubmitting ? (
                        <>
                            <LoaderCircle aria-hidden='true' className='size-5 motion-safe:animate-spin' />
                            Sending
                        </>
                    ) : (
                        <>
                            Send the brief
                            <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover/button:rotate-45' />
                        </>
                    )}
                </button>
            </div>

            <p aria-live='polite' className='sr-only'>
                {isSubmitting ? 'Sending your brief.' : ''}
            </p>
        </form>
    )
}

export default ContactForm
